// src/components/workspace/WorkspaceTree.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TreeView, TreeItem } from "@mui/lab";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Stack,
  Chip,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Divider,

} from "@mui/material";
import {
  ExpandMore,
  ChevronRight,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Keyboard as KeyboardIcon,
  Workspaces as WorkspacesIcon,
  Folder as FolderIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Apartment as ApartmentIcon,
  Tune as TuneIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import {
  Department,
  Workspace,
  Position,
  UserPosition,
} from "../../types/models";

/** ---------- Props ---------- */
interface Props {
  workspaces: Workspace[];
  departments: Department[];
  positions: Position[];
  userPositions: UserPosition[];
  selectedWorkspace: Workspace | null;
  selectedDepartment: Department | null;
  expanded: string[];
  setExpanded: React.Dispatch<React.SetStateAction<string[]>>;

  onSelectWorkspace: (id: number) => void;
  onSelectDepartment: (id: number) => void;

  onEdit: (item: {
    id: number;
    name: string;
    type: "workspace" | "department" | "position" | "project";
  }) => void;

  onRefresh: () => void;

  onDelete:
    | ((
        item:
          | { id: number; type: "workspace" | "department" | "position" | "project" }
          | { type: "userposition"; userId: number; positionId: number }
          | { type: "userproject"; userId: number; projectId: number }
      ) => void);

  onAdd: (parent: {
    id: number;
    name: string;
    type: "workspace" | "department" | "position" | "project";
  }) => void;

  onAddUser: (parent: { parentId: number; type: "project" | "position" }) => void;
}

/** ---------- Utils ---------- */
const PREFS_KEY = "workspaceTreePrefs.v2";
const DEBOUNCE_MS = 250;

// lightweight hash -> hue for avatar backgrounds
const hashHue = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return Math.abs(h) % 360;
};

const highlight = (text: string, term: string, theme: any) => {
  if (!term) return text;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(term.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + term.length);
  const after = text.slice(idx + term.length);
  return (
    <>
      {before}
      <Box
        component="mark"
        sx={{
          px: 0.3,
          borderRadius: 0.5,
          bgcolor: theme.palette.mode === "dark" ? "warning.dark" : "warning.light",
          color: "inherit",
        }}
      >
        {match}
      </Box>
      {after}
    </>
  );
};

const useDebounced = <T,>(val: T, ms = DEBOUNCE_MS) => {
  const [debounced, setDebounced] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return debounced;
};

/** ---------- Component ---------- */
const WorkspaceTree: React.FC<Props> = ({
  workspaces,
  departments,
  positions,
  userPositions,
  expanded,
  setExpanded,
  onSelectWorkspace,
  onSelectDepartment,
  onEdit,
  onRefresh,
  onDelete,
  onAdd,
  onAddUser,
}) => {
  const theme = useTheme();
  const treeRef = useRef<HTMLDivElement>(null);

  /** ---------- View state (persisted) ---------- */
  const [showPositions, setShowPositions] = useState(true);
  const [showProjects, setShowProjects] = useState(true);
  const [showMembers, setShowMembers] = useState(true);
  const [level, setLevel] = useState(0);
  const [searchInput, setSearchInput] = useState("");

  // load persisted prefs
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      if (!raw) return;
      const prefs = JSON.parse(raw);
      if (typeof prefs.search === "string") setSearchInput(prefs.search);
      if (typeof prefs.showPositions === "boolean") setShowPositions(prefs.showPositions);
      if (typeof prefs.showProjects === "boolean") setShowProjects(prefs.showProjects);
      if (typeof prefs.showMembers === "boolean") setShowMembers(prefs.showMembers);
      if (typeof prefs.level === "number") setLevel(prefs.level);
    } catch {}
  }, []);

  // persist prefs
  useEffect(() => {
    const payload = {
      search: searchInput,
      showPositions,
      showProjects,
      showMembers,
      level,
    };
    localStorage.setItem(PREFS_KEY, JSON.stringify(payload));
  }, [searchInput, showPositions, showProjects, showMembers, level]);

  const query = useDebounced(searchInput.trim(), DEBOUNCE_MS);

  /** ---------- Indexes to speed up search & building ---------- */
  const depsByWorkspace = useMemo(() => {
    const map = new Map<number, Department[]>();
    for (const d of departments) {
      if (!map.has(d.workspaceId)) map.set(d.workspaceId, []);
      map.get(d.workspaceId)!.push(d);
    }
    return map;
  }, [departments]);

  const positionsByDept = useMemo(() => {
    const map = new Map<number, Position[]>();
    for (const p of positions) {
      if (!map.has(p.departmentId)) map.set(p.departmentId, []);
      map.get(p.departmentId)!.push(p);
    }
    return map;
  }, [positions]);

  const userPositionsByPos = useMemo(() => {
    const map = new Map<number, UserPosition[]>();
    for (const up of userPositions) {
      if (!map.has(up.positionId)) map.set(up.positionId, []);
      map.get(up.positionId)!.push(up);
    }
    return map;
  }, [userPositions]);

  /** ---------- Build nodeIds by hierarchical level ---------- */
  const getNodeIdsByLevel = useCallback((): string[][] => {
    const L0: string[] = []; // workspaces
    const L1: string[] = []; // departments
    const L2: string[] = []; // branches (positions/projects/members)
    const L3: string[] = []; // leaf children
    const L4: string[] = []; // deeper leaves (users)
    const L5: string[] = [];

    for (const w of workspaces) {
      L0.push(`workspace-${w.id}`);
      const deps = depsByWorkspace.get(w.id) || [];
      for (const d of deps) {
        L1.push(`department-${d.id}`);

        if (showPositions) {
          L2.push(`positions-${d.id}`);
          const poss = positionsByDept.get(d.id) || [];
          for (const p of poss) {
            L3.push(`position-${p.id}`);
            const ups = userPositionsByPos.get(p.id) || [];
            for (const up of ups) L4.push(`userposition-${up.id}`);
          }
        }

        if (showProjects) {
          L2.push(`projects-${d.id}`);
          const projs = (d.projects ?? []);
          for (const pr of projs) {
            L3.push(`project-${pr.id}`);
            const ups = pr.userProjects ?? [];
            for (const up of ups) L4.push(`userproject-${up.userId}-${pr.id}`);
          }
        }

        if (showMembers) {
          L2.push(`members-${d.id}`);
          const mems = d.members ?? [];
          for (const m of mems) L3.push(`member-${m.id}`);
        }
      }
    }

    return [L0, L1, L2, L3, L4, L5];
  }, [workspaces, depsByWorkspace, positionsByDept, userPositionsByPos, showPositions, showProjects, showMembers]);

  /** ---------- Expand/collapse helpers ---------- */
  const expandToLevel = useCallback(
    (targetLevel: number) => {
      const levels = getNodeIdsByLevel();
      const clamped = Math.max(0, Math.min(targetLevel, levels.length - 1));
      const expandedAll = levels.slice(0, clamped + 1).flat();
      setExpanded(expandedAll);
      setLevel(clamped);
    },
    [getNodeIdsByLevel, setExpanded]
  );

  const handleExpandOne = () => expandToLevel(level + 1);
  const handleCollapseOne = () => expandToLevel(level - 1);
  const handleExpandAll = () => expandToLevel(5);
  const handleCollapseAll = () => expandToLevel(0);

  /** ---------- Search (match list + navigation) ---------- */
  const [matches, setMatches] = useState<string[]>([]);
  const [matchIdx, setMatchIdx] = useState(0);

  const computeMatches = useCallback(
    (term: string) => {
      if (!term) return { ids: [] as string[], expand: [] as string[] };

      const words = term.toLowerCase().split(" ").filter(Boolean);
      const contains = (s: string) => {
        const lower = s.toLowerCase();
        return words.every((w) => lower.includes(w));
      };

      const ids: string[] = [];
      const expandSet = new Set<string>();

      for (const w of workspaces) {
        if (contains(w.name)) {
          ids.push(`workspace-${w.id}`);
        }
        const deps = depsByWorkspace.get(w.id) || [];
        for (const d of deps) {
          if (contains(d.name)) {
            ids.push(`department-${d.id}`);
            expandSet.add(`workspace-${w.id}`);
          }

          if (showPositions) {
            expandSet.add(`positions-${d.id}`);
            const poss = positionsByDept.get(d.id) || [];
            for (const p of poss) {
              if (contains(p.name)) {
                ids.push(`position-${p.id}`);
                expandSet.add(`workspace-${w.id}`);
                expandSet.add(`department-${d.id}`);
              }
              const ups = userPositionsByPos.get(p.id) || [];
              for (const up of ups) {
                const uname = up.user?.username ?? "";
                const pname = up.user?.profile?.name ?? "";
                const full = `${uname} ${pname}`.trim();
                if (full && contains(full)) {
                  ids.push(`userposition-${up.id}`);
                  expandSet.add(`workspace-${w.id}`);
                  expandSet.add(`department-${d.id}`);
                  expandSet.add(`positions-${d.id}`);
                  expandSet.add(`position-${p.id}`);
                }
              }
            }
          }

          if (showProjects) {
            expandSet.add(`projects-${d.id}`);
            const projs = d.projects ?? [];
            for (const pr of projs) {
              if (contains(pr.name)) {
                ids.push(`project-${pr.id}`);
                expandSet.add(`workspace-${w.id}`);
                expandSet.add(`department-${d.id}`);
              }
              const ups = pr.userProjects ?? [];
              for (const up of ups) {
                const uname = up.user?.username ?? "";
                const pname = up.user?.profile?.name ?? "";
                const full = `${uname} ${pname}`.trim();
                if (full && contains(full)) {
                  ids.push(`userproject-${up.userId}-${pr.id}`);
                  expandSet.add(`workspace-${w.id}`);
                  expandSet.add(`department-${d.id}`);
                  expandSet.add(`projects-${d.id}`);
                  expandSet.add(`project-${pr.id}`);
                }
              }
            }
          }

          if (showMembers) {
            expandSet.add(`members-${d.id}`);
            const mems = d.members ?? [];
            for (const m of mems) {
              const uname = m.username ?? "";
              const pname = m.profile?.name ?? "";
              const full = `${uname} ${pname}`.trim();
              if (full && contains(full)) {
                ids.push(`member-${m.id}`);
                expandSet.add(`workspace-${w.id}`);
                expandSet.add(`department-${d.id}`);
                expandSet.add(`members-${d.id}`);
              }
            }
          }
        }
      }

      return { ids, expand: Array.from(expandSet) };
    },
    [depsByWorkspace, positionsByDept, userPositionsByPos, showPositions, showProjects, showMembers]
  );

  useEffect(() => {
    const { ids, expand } = computeMatches(query);
    setMatches(ids);
    setMatchIdx(0);
    if (expand.length) {
      setExpanded((prev) => Array.from(new Set([...prev, ...expand])));
    }
  }, [query, computeMatches, setExpanded]);

  useEffect(() => {
    if (!matches.length) return;
    const id = matches[matchIdx]!;
    const el = document.querySelector(`[data-nodeid="${id}"]`);
    if (el && treeRef.current) {
      const box = treeRef.current;
      const rect = (el as HTMLElement).getBoundingClientRect();
      const boxRect = box.getBoundingClientRect();
      if (rect.top < boxRect.top || rect.bottom > boxRect.bottom) {
        box.scrollTo({
          top: box.scrollTop + (rect.top - boxRect.top) - 64,
          behavior: "smooth",
        });
      }
    }
  }, [matches, matchIdx]);

  const isMatch = useCallback((nodeId: string) => matches.includes(nodeId), [matches]);

  /** ---------- Context menu ---------- */
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuItem, setMenuItem] = useState<{
    type: "workspace" | "department" | "position" | "project" | "userposition" | "userproject" | "member";
    id?: number | string;
    name?: string;
    extra?: any;
  } | null>(null);

  const openMenu = Boolean(menuAnchor);
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  /** ---------- Toolbar actions ---------- */
  const clearSearch = () => setSearchInput("");
  const nextMatch = () => setMatchIdx((i) => (matches.length ? (i + 1) % matches.length : 0));
  const prevMatch = () => setMatchIdx((i) => (matches.length ? (i - 1 + matches.length) % matches.length : 0));

  /** ---------- Render helpers: Labels ---------- */
  const LabelRow: React.FC<{
    icon?: React.ReactNode;
    emoji?: string;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    matched?: boolean;
    onAdd?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onMore?: (e: React.MouseEvent<HTMLElement>) => void;
  }> = ({ icon, emoji, title, subtitle, matched, onAdd, onEdit, onDelete, onMore }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 0.5,
        py: 0.25,
        borderRadius: 1,
        ...(matched ? {
          bgcolor: theme.palette.mode === "dark" ? "warning.dark" : "warning.light",
        } : {}),
        "& .actions": { opacity: 0, transform: "translateY(-2px)", transition: "all .15s" },
        "&:hover .actions": { opacity: 1, transform: "translateY(0)" },
      }}
    >
      {emoji ? <span style={{ width: 20 }}>{emoji}</span> : icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap title={typeof title === "string" ? title : undefined}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} className="actions">
        {onAdd && (
          <Tooltip title="Add">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onAdd(); }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onMore && (
          <Tooltip title="More">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onMore(e); }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );

  /** ---------- Component ---------- */
  return (
    <Box>
      {/* Toolbar */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          {/* Left */}
          <Stack direction="row" spacing={1} alignItems="center" flex={1} minWidth={280}>
            <WorkspacesIcon color="primary" />
            <Typography variant="h6" fontWeight={800}>
              Workspace Explorer
            </Typography>
            <Chip
              size="small"
              label={`${workspaces.length} WS • ${departments.length} Dept • ${positions.length} Pos`}
            />
          </Stack>

          {/* Middle */}
          <Stack direction="row" spacing={1.5} alignItems="center" flex={2}>
            <TextField
              size="small"
              fullWidth
              inputProps={{ "aria-label": "Search in tree" }}
              placeholder="Search (Ctrl/Cmd + K)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <Tooltip title="Clear">
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            {/* Match nav */}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 160 }}>
              <Chip
                size="small"
                variant="outlined"
                label={`Matches: ${matches.length ? `${matchIdx + 1}/${matches.length}` : "0"}`}
              />
              <Tooltip title="Previous match">
                <span>
                  <IconButton size="small" disabled={!matches.length} onClick={prevMatch}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next match">
                <span>
                  <IconButton size="small" disabled={!matches.length} onClick={nextMatch}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Right */}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flex={1}>
            <Tooltip title="Collapse one level">
              <IconButton size="small" onClick={handleCollapseOne}>
                <ExpandLessIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Expand one level">
              <IconButton size="small" onClick={handleExpandOne}>
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse all">
              <Button size="small" variant="outlined" onClick={handleCollapseAll}>
                Collapse All
              </Button>
            </Tooltip>
            <Tooltip title="Expand all">
              <Button size="small" variant="contained" onClick={handleExpandAll}>
                Expand All
              </Button>
            </Tooltip>

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

            {/* Branch visibility chips */}
            <Tooltip title="Toggle branches">
              <TuneIcon fontSize="small" color="action" />
            </Tooltip>
            <Chip
              size="small"
              icon={<ApartmentIcon />}
              label="Positions"
              variant={showPositions ? "filled" : "outlined"}
              color={showPositions ? "primary" : "default"}
              onClick={() => setShowPositions((v) => !v)}
            />
            <Chip
              size="small"
              icon={<FolderIcon />}
              label="Projects"
              variant={showProjects ? "filled" : "outlined"}
              color={showProjects ? "primary" : "default"}
              onClick={() => setShowProjects((v) => !v)}
            />
            <Chip
              size="small"
              icon={<PeopleIcon />}
              label="Members"
              variant={showMembers ? "filled" : "outlined"}
              color={showMembers ? "primary" : "default"}
              onClick={() => setShowMembers((v) => !v)}
            />

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

            <Tooltip title="Refresh data">
              <IconButton onClick={onRefresh} size="small">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Keyboard hints */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, opacity: 0.8 }}>
          <KeyboardIcon fontSize="small" />
          <Typography variant="caption">
            <b>Ctrl/Cmd + K</b> search • <b>↑/↓</b> navigate • <b>Enter</b> open/edit • <b>Del</b> delete
          </Typography>
        </Stack>
      </Paper>

      {/* Tree */}
      <Box ref={treeRef} sx={{ maxHeight: 600, overflowY: "auto", borderRadius: 2 }}>
        <TreeView
          expanded={expanded}
          onNodeToggle={(_, nodeIds) => setExpanded(nodeIds)}
          defaultCollapseIcon={<ExpandMore />}
          defaultExpandIcon={<ChevronRight />}
          sx={{ flexGrow: 1, px: 0.5 }}
        >
          {workspaces.map((ws) => {
            const wsNodeId = `workspace-${ws.id}`;
            const wsMatched = isMatch(wsNodeId);
            const deps = depsByWorkspace.get(ws.id) || [];

            return (
              <TreeItem
                key={wsNodeId}
                nodeId={wsNodeId}
                label={
                  <LabelRow
                    emoji="🏢"
                    title={highlight(ws.name, query, theme)}
                    subtitle={
                      <Stack direction="row" spacing={1}>
                        <Chip size="small" variant="outlined" label={`${deps.length} dept`} />
                      </Stack>
                    }
                    matched={wsMatched}
                    onAdd={() => onAdd({ id: ws.id, name: ws.name, type: "workspace" })}
                    onEdit={() => onEdit({ id: ws.id, name: ws.name, type: "workspace" })}
                    onDelete={() => onDelete({ id: ws.id, type: "workspace" })}
                    onMore={(e) => {
                      setMenuItem({ type: "workspace", id: ws.id, name: ws.name });
                      setMenuAnchor(e.currentTarget);
                    }}
                  />
                }
                onClick={() => {
                  onSelectWorkspace(ws.id);
                  setExpanded((prev) => (prev.includes(wsNodeId) ? prev : [...prev, wsNodeId]));
                }}
              >
                {deps.map((dept) => {
                  const dNodeId = `department-${dept.id}`;
                  const dMatched = isMatch(dNodeId);
                  const poss = positionsByDept.get(dept.id) || [];
                  const projs = dept.projects ?? [];
                  const mems = dept.members ?? [];

                  return (
                    <TreeItem
                      key={dNodeId}
                      nodeId={dNodeId}
                      label={
                        <LabelRow
                          emoji="🧩"
                          title={highlight(dept.name, query, theme)}
                          subtitle={
                            <Stack direction="row" spacing={1}>
                              {showPositions && <Chip size="small" variant="outlined" label={`${poss.length} pos`} />}
                              {showProjects && <Chip size="small" variant="outlined" label={`${projs.length} proj`} />}
                              {showMembers && <Chip size="small" variant="outlined" label={`${mems.length} mem`} />}
                            </Stack>
                          }
                          matched={dMatched}
                          onEdit={() => onEdit({ id: dept.id, name: dept.name, type: "department" })}
                          onDelete={() => onDelete({ id: dept.id, type: "department" })}
                          onMore={(e) => {
                            setMenuItem({ type: "department", id: dept.id, name: dept.name });
                            setMenuAnchor(e.currentTarget);
                          }}
                        />
                      }
                      onClick={() => {
                        onSelectDepartment(dept.id);
                        setExpanded((prev) => (prev.includes(dNodeId) ? prev : [...prev, dNodeId]));
                      }}
                    >
                      {/* Positions branch */}
                      {showPositions && (
                        <TreeItem
                          nodeId={`positions-${dept.id}`}
                          label={
                            <LabelRow
                              icon={<ApartmentIcon fontSize="small" />}
                              title={<Typography variant="body2">Positions</Typography>}
                              subtitle={<Typography variant="caption">{poss.length} total</Typography>}
                              onAdd={() => onAdd({ id: dept.id, name: dept.name, type: "department" })}
                            />
                          }
                        >
                          {poss.map((pos) => {
                            const pNodeId = `position-${pos.id}`;
                            const pMatched = isMatch(pNodeId);
                            const ups = userPositionsByPos.get(pos.id) || [];
                            return (
                              <TreeItem
                                key={pNodeId}
                                nodeId={pNodeId}
                                label={
                                  <LabelRow
                                    emoji="💼"
                                    title={highlight(pos.name, query, theme)}
                                    subtitle={<Typography variant="caption">{ups.length} assigned</Typography>}
                                    matched={pMatched}
                                    onAdd={() => onAddUser({ parentId: pos.id, type: "position" })}
                                    onEdit={() => onEdit({ id: pos.id, name: pos.name, type: "position" })}
                                    onDelete={() => onDelete({ id: pos.id, type: "position" })}
                                    onMore={(e) => {
                                      setMenuItem({ type: "position", id: pos.id, name: pos.name });
                                      setMenuAnchor(e.currentTarget);
                                    }}
                                  />
                                }
                              >
                                {ups.map((up) => {
                                  const upNodeId = `userposition-${up.id}`;
                                  const upMatched = isMatch(upNodeId);
                                  const uname = up.user?.username || `User #${String(up.id).slice(0, 6)}`;
                                  const avatar = up.user?.profile?.imageUrl;
                                  const hue = hashHue(uname);
                                  const bg =
                                    theme.palette.mode === "dark"
                                      ? `hsl(${hue} 50% 22%)`
                                      : `hsl(${hue} 85% 90%)`;
                                  return (
                                    <TreeItem
                                      key={upNodeId}
                                      nodeId={upNodeId}
                                      label={
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                          sx={{
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: 1,
                                            ...(upMatched
                                              ? {
                                                  bgcolor:
                                                    theme.palette.mode === "dark"
                                                      ? "warning.dark"
                                                      : "warning.light",
                                                }
                                              : {}),
                                          }}
                                        >
                                          <Avatar
                                            src={avatar || undefined}
                                            sx={{ width: 22, height: 22, bgcolor: bg }}
                                          >
                                            <PersonIcon fontSize="inherit" />
                                          </Avatar>
                                          <Typography variant="body2" sx={{ flex: 1 }} noWrap title={uname}>
                                            {highlight(uname, query, theme)}
                                          </Typography>
                                          <Tooltip title="Remove from position">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (up.user?.id !== undefined) {
                                                  onDelete({
                                                    type: "userposition",
                                                    userId: up.user.id,
                                                    positionId: up.positionId,
                                                  });
                                                }
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                      }
                                    />
                                  );
                                })}
                              </TreeItem>
                            );
                          })}
                        </TreeItem>
                      )}

                      {/* Projects branch */}
                      {showProjects && (
                        <TreeItem
                          nodeId={`projects-${dept.id}`}
                          label={
                            <LabelRow
                              icon={<FolderIcon fontSize="small" />}
                              title={<Typography variant="body2">Projects</Typography>}
                              subtitle={<Typography variant="caption">{(dept.projects ?? []).length} total</Typography>}
                              onAdd={() => onAdd({ id: dept.id, name: dept.name, type: "project" })}
                            />
                          }
                        >
                          {(dept.projects ?? []).map((pr) => {
                            const prNodeId = `project-${pr.id}`;
                            const prMatched = isMatch(prNodeId);
                            const ups = pr.userProjects ?? [];
                            return (
                              <TreeItem
                                key={prNodeId}
                                nodeId={prNodeId}
                                label={
                                  <LabelRow
                                    emoji="📂"
                                    title={highlight(pr.name, query, theme)}
                                    subtitle={<Typography variant="caption">{ups.length} members</Typography>}
                                    matched={prMatched}
                                    onAdd={() => onAddUser({ parentId: pr.id, type: "project" })}
                                    onEdit={() => onEdit({ id: pr.id, name: pr.name, type: "project" })}
                                    onDelete={() => onDelete({ id: pr.id, type: "project" })}
                                    onMore={(e) => {
                                      setMenuItem({ type: "project", id: pr.id, name: pr.name });
                                      setMenuAnchor(e.currentTarget);
                                    }}
                                  />
                                }
                              >
                                {ups.map((up) => {
                                  const upNodeId = `userproject-${up.userId}-${pr.id}`;
                                  const upMatched = isMatch(upNodeId);
                                  const uname = up.user?.username || `User #${up.userId}`;
                                  const avatar = up.user?.profile?.imageUrl;
                                  const hue = hashHue(uname);
                                  const bg =
                                    theme.palette.mode === "dark"
                                      ? `hsl(${hue} 50% 22%)`
                                      : `hsl(${hue} 85% 90%)`;
                                  return (
                                    <TreeItem
                                      key={upNodeId}
                                      nodeId={upNodeId}
                                      label={
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                          sx={{
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: 1,
                                            ...(upMatched
                                              ? {
                                                  bgcolor:
                                                    theme.palette.mode === "dark"
                                                      ? "warning.dark"
                                                      : "warning.light",
                                                }
                                              : {}),
                                          }}
                                        >
                                          <Avatar
                                            src={avatar || undefined}
                                            sx={{ width: 22, height: 22, bgcolor: bg }}
                                          >
                                            <PersonIcon fontSize="inherit" />
                                          </Avatar>
                                          <Typography variant="body2" sx={{ flex: 1 }} noWrap title={uname}>
                                            {highlight(uname, query, theme)}
                                          </Typography>
                                          <Tooltip title="Remove from project">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete({
                                                  type: "userproject",
                                                  userId: up.userId,
                                                  projectId: pr.id,
                                                });
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                      }
                                    />
                                  );
                                })}
                              </TreeItem>
                            );
                          })}
                        </TreeItem>
                      )}

                      {/* Members branch */}
                      {showMembers && (
                        <TreeItem
                          nodeId={`members-${dept.id}`}
                          label={
                            <LabelRow
                              icon={<PeopleIcon fontSize="small" />}
                              title={<Typography variant="body2">Members</Typography>}
                              subtitle={<Typography variant="caption">{(dept.members ?? []).length} people</Typography>}
                            />
                          }
                        >
                          {(dept.members ?? []).map((m) => {
                            const mNodeId = `member-${m.id}`;
                            const mMatched = isMatch(mNodeId);
                            const uname = m.username || `User #${m.id}`;
                            const avatar = m.profile?.imageUrl;
                            const hue = hashHue(uname);
                            const bg =
                              theme.palette.mode === "dark"
                                ? `hsl(${hue} 50% 22%)`
                                : `hsl(${hue} 85% 90%)`;
                            return (
                              <TreeItem
                                key={mNodeId}
                                nodeId={mNodeId}
                                label={
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{
                                      px: 0.5,
                                      py: 0.25,
                                      borderRadius: 1,
                                      ...(mMatched
                                        ? {
                                            bgcolor:
                                              theme.palette.mode === "dark"
                                                ? "warning.dark"
                                                : "warning.light",
                                          }
                                        : {}),
                                    }}
                                  >
                                    <Avatar src={avatar || undefined} sx={{ width: 22, height: 22, bgcolor: bg }}>
                                      <PersonIcon fontSize="inherit" />
                                    </Avatar>
                                    <Typography variant="body2" noWrap title={uname}>
                                      {highlight(uname, query, theme)}
                                    </Typography>
                                  </Stack>
                                }
                              />
                            );
                          })}
                        </TreeItem>
                      )}
                    </TreeItem>
                  );
                })}
              </TreeItem>
            );
          })}
        </TreeView>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={openMenu}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2">
            {menuItem?.name || (menuItem?.type ? menuItem.type.toUpperCase() : "")}
          </Typography>
        </Box>
        <Divider />
        {menuItem?.type && ["workspace", "department", "position", "project"].includes(menuItem.type) && (
          <>
            <MenuItem
              onClick={() => {
                if (!menuItem) return;
                onEdit({
                  id: Number(menuItem.id),
                  name: String(menuItem.name || ""),
                  type: menuItem.type as any,
                });
                closeMenu();
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (!menuItem) return;
                onDelete({ id: Number(menuItem.id), type: menuItem.type as any });
                closeMenu();
              }}
              sx={{ color: "error.main" }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </>
        )}

        {menuItem?.type === "userposition" && menuItem.extra && (
          <MenuItem
            onClick={() => {
              onDelete({
                type: "userposition",
                userId: menuItem.extra.userId,
                positionId: menuItem.extra.positionId,
              });
              closeMenu();
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Remove from position
          </MenuItem>
        )}

        {menuItem?.type === "userproject" && menuItem.extra && (
          <MenuItem
            onClick={() => {
              onDelete({
                type: "userproject",
                userId: menuItem.extra.userId,
                projectId: menuItem.extra.projectId,
              });
              closeMenu();
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Remove from project
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default WorkspaceTree;
